'use server'

import { AuthError } from 'next-auth'
import { getLocale } from 'next-intl/server'
import { emailSchema } from '@shipman/core'
import { createOtp } from '@/lib/auth/otp'
import { signIn } from '@/lib/auth/auth'
import { sendOtpEmail } from '@/lib/email/send-otp-email'
import { safeCallbackUrl } from '@/lib/actions/safe-callback-url'
import type { Locale } from '@/i18n/config'

export type SignInError = 'invalidOtp' | 'invalidCredentials' | 'sendFailed' | 'generic'
export type SignInResult = { ok: true } | { ok: false; error: SignInError }

// ===============================
// SECTION: EMAIL OTP
// ===============================

export async function requestOtpAction(rawEmail: string): Promise<SignInResult> {
	const parsed = emailSchema.safeParse(rawEmail)
	if (!parsed.success) return { ok: false, error: 'sendFailed' }

	try {
		const { token } = await createOtp(parsed.data)
		await sendOtpEmail({ to: parsed.data, code: token, locale: (await getLocale()) as Locale })
		return { ok: true }
	} catch (error) {
		console.error('[otp] sending the code failed', error)
		return { ok: false, error: 'sendFailed' }
	}
}

interface OtpSignInInput {
	email: string
	otp: string
	callbackUrl?: string
}

export async function signInWithOtpAction(input: OtpSignInInput): Promise<SignInResult> {
	return runSignIn('email-otp', { email: input.email, otp: input.otp }, input.callbackUrl, 'invalidOtp')
}

// ===============================
// SECTION: USERNAME / PASSWORD
// ===============================

interface PasswordSignInInput {
	username: string
	password: string
	callbackUrl?: string
}

export async function signInWithPasswordAction(input: PasswordSignInInput): Promise<SignInResult> {
	return runSignIn(
		'password',
		{ username: input.username, password: input.password },
		input.callbackUrl,
		'invalidCredentials',
	)
}

// ===============================
// SECTION: OAUTH
// ===============================

export async function signInWithProviderAction(provider: 'google' | 'github', callbackUrl?: string): Promise<void> {
	await signIn(provider, { redirectTo: safeCallbackUrl(callbackUrl) })
}

// ===============================
// SECTION: SHARED
// ===============================

async function runSignIn(
	provider: string,
	credentials: Record<string, string>,
	callbackUrl: string | undefined,
	credentialError: SignInError,
): Promise<SignInResult> {
	try {
		await signIn(provider, { ...credentials, redirectTo: safeCallbackUrl(callbackUrl) })
		return { ok: true }
	} catch (error) {
		if (error instanceof AuthError) {
			return { ok: false, error: error.type === 'CredentialsSignin' ? credentialError : 'generic' }
		}
		// INFO: Successful sign-in redirects by throwing NEXT_REDIRECT, which must propagate
		throw error
	}
}
